// src/pages/DBCLandingPage.tsx
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import CheckIcon from "../../../components/common/CheckIcon";
import founder from "../../../assets/images/Blurred shirt.png";
import { useCreateLeadMutation } from "../../../app/api/leadApi";

/**
 * Minor optimizations:
 * - Move static content (pain list) out of the component so it's not re-created on every render.
 * - Keep structure and text identical — only the "pain" block is moved out of the hero into its own section.
 */

const PAINS = [
  "The Job Insecurity Fear: 'What if I lose my job tomorrow?'",
  "The Time Trap: 'I'm too busy to start something new'",
  "The Confusion Paralysis: 'Which idea will actually make money?'",
  "The Tech Overwhelm: 'I don't know where to start'",
  "The Proof Problem: 'Who will buy from me?'",
];

const DBCModule1: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: "",
    email: "",
    phone: "",
  });
  const [spotsRemaining, setSpotsRemaining] = useState<number>(23);

  // RTK Query mutation hook (create-new endpoint)
  const [createLead, { isLoading }] = useCreateLeadMutation();

  // local UI states
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((s) => ({ ...s, [e.target.name]: e.target.value }));
  };

  // Basic phone sanitiser (keeps digits + leading +)
  const normalizePhone = (raw: string) => {
    if (!raw) return "";
    return raw.replace(/[^+\d]/g, "");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    setSuccessMessage(null);

    // minimal validation
    const { firstName, email, phone } = formData;
    if (!firstName.trim()) {
      setErrorMessage("Please enter your full name.");
      return;
    }
    if (!email.trim()) {
      setErrorMessage("Please enter your email address.");
      return;
    }
    // basic email check
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setErrorMessage("Please enter a valid email address.");
      return;
    }
    if (!phone.trim()) {
      setErrorMessage("Please enter your WhatsApp number.");
      return;
    }

    // build payload

    const payload = {
      firstName,
      email: email.trim().toLowerCase(),
      phone: normalizePhone(phone),
      source: "dbc_landing_page",
    };

    try {
      const result = await createLead(payload).unwrap();
      if (result && result.success) {
        setSuccessMessage(
          "Success! Your spot is reserved. Check your email and WhatsApp for confirmation."
        );

        navigate("/dbc/dib-webinar");

        // Decrement spots locally (optimistic)
        setSpotsRemaining((s) => Math.max(0, s - 1));

        // keep form disabled after success (optional)
        // clear form but keep success message
        setFormData((f) => ({ ...f, firstName: "", email: "", phone: "" }));
      } else {
        // backend returned success: false
        setErrorMessage(
          result?.message || "Could not register. Please try again."
        );
      }
    } catch (err: any) {
      console.error("Lead create error:", err);
      // display friendly message
      setErrorMessage(
        err?.data?.message ||
          err?.message ||
          "An error occurred while submitting. Please try again."
      );
    }
  };

  // const testimonials = [
  //   {
  //     name: "Chiamaka O.",
  //     role: "HR Manager, Lagos",
  //     image: founder,
  //     text: "I was skeptical. I've bought courses before that gathered dust on my computer. But DBC Formula is different. Within 3 weeks, I had my first 20 paying customers. I made ₦180,000 in my first month! I'm still working my 9-5, but now I have a safety net. And it only takes me 2 hours a day.",
  //   },
  //   {
  //     name: "Emeka I.",
  //     role: "IT Consultant, Abuja",
  //     image: founder,
  //     text: "The SweetSpot Formula alone was worth 10x the course fee. I spent 6 months trying to figure out 'what to sell.' After Module 1, I had total clarity. After Module 3, I had my first automated funnel making sales while I slept. Current side income: ₦350k/month.",
  //   },
  //   {
  //     name: "Fatima M.",
  //     role: "Banking Professional, Port Harcourt",
  //     image: founder,
  //     text: "As a mother of 3 with a demanding career, I didn't think I had time. But the system is designed for people like us. I batch my content creation on Sundays, and everything else is automated. My WhatsApp list went from 0 to 400 people in 6 weeks. Revenue: ₦420k last month.",
  //   },
  // ];

  const faqs = [
    {
      q: "Is this really free?",
      a: "Yes, 100% free. Module 1 is our gift to you. If you love it and want to continue with Modules 2 & 3 (Build & Convert), we'll make you an offer. But there's zero obligation.",
    },
    {
      q: "I'm very busy. Will this work for me?",
      a: "This system was DESIGNED for busy professionals. Everything is batched, automated, and time-efficient. You'll need 2-3 hours per week maximum.",
    },
    {
      q: "I don't have a business idea yet. Can I still attend?",
      a: "Absolutely! That's exactly what Module 1 helps you discover. Come with an open mind; leave with a validated business idea.",
    },
    {
      q: "What if I can't make it live?",
      a: "The replay will be available for 48 hours, but you'll miss the live feedback and community interaction which are the most valuable parts.",
    },
    {
      q: "I've tried online business before and failed. Why will this be different?",
      a: "Because most people start building before they discover their sweet spot. They create content before building an audience. They try to sell before establishing trust. DBC Formula fixes all of that with a proven sequence that works.",
    },
    {
      q: "Do I need technical skills?",
      a: "No. If you can use WhatsApp and email, you can do this. We'll show you the simple tools and exact steps.",
    },
  ];

  const whatYouGet = [
    {
      icon: "🎯",
      title: "The SweetSpot Formula Workshop",
      value: "₦25,000",
      items: [
        "Map your most profitable skills in 30 minutes using the PRO framework",
        "Discover which of your passions can actually make money",
        "The 'Hidden Goldmine' exercise that reveals untapped revenue opportunities",
      ],
    },
    {
      icon: "🎯",
      title: "Customer Avatar Deep Dive",
      value: "₦20,000",
      items: [
        "Create a laser-focused avatar in 45 minutes using the Greatest Desire vs. Greatest Pain Matrix",
        "Learn the Nigerian market cultural codes that make people buy",
        "Get the exact income-level targeting strategy for paying customers",
      ],
    },
    {
      icon: "🎯",
      title: "Live Offer Creation Session",
      value: "₦30,000",
      items: [
        "Build your irresistible offer LIVE using the Value Stacking Framework",
        "Get real-time feedback on your pricing from me and other participants",
        "Walk away with offer copy you can test THIS WEEK",
      ],
    },
    {
      icon: "🎁",
      title: "BONUS: The MVP Validation Toolkit",
      value: "₦15,000",
      items: [
        "Simple tests to validate your idea in 72 hours WITHOUT building anything",
        "The 'First 10 Customers' roadmap for people with zero audience",
        "Access to our private community for 30 days",
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-white text-brandNavy">
      {/* Hero Section - PROBLEM */}
      <section className="relative bg-gradient-to-br from-brandNavy via-brandNavy to-brandLightNavy text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: "url('/assets/images/hero-pattern.svg')",
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 py-10 lg:py-20">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <div className="inline-block bg-brandRed/20 text-white px-4 py-2 rounded-full text-base font-semibold mb-6">
                🔥 Free Training Session • Limited Spots Available
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight mb-6">
                You're One Paycheck Away From Broke... But One Skill Away From
                Rich
              </h1>

              <p className="text-2xl md:text-2xl text-white/90 mb-8 leading-relaxed">
                While you're building someone else's empire for a "stable"
                salary, people with LESS experience than you are making ₦1M+
                monthly from home. The difference? They stopped waiting for
                permission to monetize their expertise.
              </p>

              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 mb-8">
                <p className="text-lg md:text-2xl text-white/95 mb-4">
                  <strong>
                    And right now, your skills and knowledge are making money
                    for someone else—not you.
                  </strong>
                </p>
                <p className="text-base md:text-lg text-white/80">
                  You're talented. You're experienced. You've spent years
                  building expertise that people desperately need.
                </p>
              </div>

              <div className="space-y-3 mb-8">
                {/* Removed pain list from here per request — it's now in its own section below */}
              </div>

              <a
                href="#register"
                className="inline-block bg-brandRed text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-red-700 transition-all transform hover:scale-105 shadow-xl"
              >
                Reserve My FREE Spot Now →
              </a>
            </div>

            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src={founder}
                  alt="Professional working on laptop"
                  className="w-full h-auto"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-brandNavy/50 to-transparent" />

                {/* Floating Stats - Now on the image */}
                <div className="absolute bottom-6 left-6 bg-white text-brandNavy p-6 rounded-xl shadow-2xl">
                  <div className="text-3xl font-bold text-brandRed">₦7.2M+</div>
                  <div className="text-sm text-mutedGray">
                    Left on the table every year
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* NEW: Pain Section (moved out of hero) */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-white/0 rounded-lg p-6">
            <div className="space-y-3 mb-8">
              {PAINS.map((pain, i) => (
                <div
                  key={i}
                  className="flex items-start gap-3 text-brandNavy text-base md:text-lg"
                >
                  <span className="text-brandRed text-2xl">❌</span>
                  <span>{pain}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* AMPLIFY Section */}
      <section className="py-20 bg-red-50">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-center mb-8">
            How Much Longer Will You Keep Building Someone Else's Dream?
          </h2>

          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg mb-12">
            <p className="text-2xl md:text-2xl mb-6 text-center font-semibold">
              Let's do the math together:
            </p>

            <div className="bg-brandLightNavy/5 border-2 border-brandNavy/10 rounded-xl p-8 mb-8">
              <p className="text-lg md:text-2xl mb-4">
                If you could monetize just ONE of your skills and make an extra{" "}
                <strong className="text-brandRed">₦200,000 per month</strong>{" "}
                (that's just ₦50,000 per week)...
              </p>

              <div className="grid md:grid-cols-3 gap-6 mt-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-brandNavy">₦1.2M</div>
                  <div className="text-base text-mutedGray">In 6 months</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-brandNavy">₦2.4M</div>
                  <div className="text-base text-mutedGray">In 1 year</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-brandRed">₦7.2M</div>
                  <div className="text-base text-mutedGray font-semibold">
                    In 3 years
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center text-2xl md:text-3xl font-bold text-brandRed mb-8">
              That's over 7 MILLION NAIRA you're leaving on the table.
            </div>

            <h3 className="text-2xl md:text-3xl font-bold mb-6 text-center">
              But it gets worse...
            </h3>

            <div className="space-y-6">
              <div className="bg-red-50 border-l-4 border-brandRed p-6 rounded-r-lg">
                <p className="font-semibold mb-2 text-lg">
                  While You're Waiting:
                </p>
                <ul className="space-y-3 text-mutedGray text-base md:text-lg">
                  <li className="flex items-start gap-3">
                    <span className="text-brandRed mt-1">▸</span>
                    <span>
                      Your colleague who started that side hustle 6 months ago?
                      They just quit their job. Their online business is
                      bringing in ₦500k monthly.
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-brandRed mt-1">▸</span>
                    <span>
                      That person who asked for your advice? They took action.
                      Now they're earning more than their salary while you're
                      still "planning."
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-brandRed mt-1">▸</span>
                    <span>
                      Your bills keep increasing, but your salary isn't keeping
                      pace with inflation.
                    </span>
                  </li>
                </ul>
              </div>

              <div className="bg-brandNavy text-white p-8 rounded-xl">
                <p className="text-2xl md:text-2xl font-semibold mb-4">
                  Here's the brutal truth:
                </p>
                <p className="text-lg md:text-2xl mb-4">
                  The skills and knowledge in your head right now could be
                  generating income 24/7. Instead, they're locked away, unused,
                  while you struggle with one income stream that could disappear
                  tomorrow.
                </p>
                <p className="text-white/90 text-base md:text-lg">
                  The question isn't whether you'll start an online business.
                  It's whether you'll start{" "}
                  <strong>before it's too late.</strong>
                </p>
              </div>
            </div>
          </div>

          <div className="text-center">
            <a
              href="#register"
              className="inline-block bg-brandRed text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-red-700 transition-all transform hover:scale-105 shadow-xl"
            >
              I'm Ready to Take Action →
            </a>
          </div>
        </div>
      </section>

      {/* STORY Section */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              I Was Just Like You... Until I Discovered The 3-Step System That
              Changed Everything
            </h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 mb-12">
            <div>
              <img
                src={founder}
                alt="Founder story"
                className="w-full h-auto rounded-2xl shadow-lg"
              />
            </div>

            <div className="space-y-4 text-lg md:text-2xl">
              <p>
                Three years ago, I was a frustrated professional, just like you.
              </p>
              <p>Good job. Decent salary. But trapped.</p>
              <p>
                I knew I had valuable skills—years of expertise that people
                needed. But every time I tried to "start something online," I
                hit a wall:
              </p>

              <div className="space-y-2 my-6">
                {[
                  "Spent months building a course nobody bought",
                  "Created content that got zero engagement",
                  "Tried running ads that drained my savings with nothing to show",
                  "Started and abandoned 5 different 'business ideas'",
                ].map((item, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-3 text-mutedGray text-base md:text-lg"
                  >
                    <span className="text-brandRed">❌</span>
                    <span>{item}</span>
                  </div>
                ))}
              </div>

              <p className="font-semibold text-brandNavy">
                I was doing EVERYTHING... except making money.
              </p>
            </div>
          </div>

          <div className="bg-gradient-to-br from-brandNavy to-brandLightNavy text-white rounded-2xl p-8 md:p-12 mb-12">
            <h3 className="text-2xl md:text-3xl font-bold mb-6">
              Then I had a breakthrough.
            </h3>
            <p className="text-lg md:text-2xl mb-6">
              I realized successful online businesses don't happen by accident.
              They follow a formula:
            </p>

            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-6">
                <div className="text-4xl font-bold text-brandRed mb-3">D</div>
                <h4 className="font-bold text-lg mb-2">DISCOVER</h4>
                <p className="text-sm md:text-base text-white/90">
                  The exact intersection of what you know, what people want, and
                  what they'll pay for
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-6">
                <div className="text-4xl font-bold text-brandRed mb-3">B</div>
                <h4 className="font-bold text-lg mb-2">BUILD</h4>
                <p className="text-sm md:text-base text-white/90">
                  Trust and visibility with the right people BEFORE trying to
                  sell
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-6">
                <div className="text-4xl font-bold text-brandRed mb-3">C</div>
                <h4 className="font-bold text-lg mb-2">CONVERT</h4>
                <p className="text-sm md:text-base text-white/90">
                  That trust into automated income streams that work while you
                  sleep
                </p>
              </div>
            </div>

            <p className="text-2xl md:text-2xl font-semibold text-center">
              D.B.C. — Discover. Build. Convert.
            </p>
          </div>

          <div className="bg-green-50 border-2 border-green-500 rounded-2xl p-8">
            <h3 className="text-2xl md:text-3xl font-bold mb-6 text-brandNavy">
              Once I understood this formula, everything changed:
            </h3>

            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  time: "Month 1",
                  result: "Built an online business (while keeping my day job)",
                },
                {
                  time: "Month 3",
                  result: "Secured ₦2.4M in sales commitments",
                },
                {
                  time: "Month 6",
                  result: "Earning more from my online business than my salary",
                },
              ].map((milestone, i) => (
                <div key={i} className="flex items-start gap-3">
                  <CheckIcon className="text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-bold text-brandNavy text-base md:text-lg">
                      {milestone.time}:
                    </div>
                    <div className="text-mutedGray text-base md:text-lg">
                      {milestone.result}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 text-center">
              <p className="text-2xl md:text-2xl font-bold text-brandNavy">
                And I did it all working just 2-3 hours per day.
              </p>
              <p className="text-mutedGray mt-2 text-base md:text-lg">
                No tech wizardry. No huge following. No quitting my job.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* OFFER Section */}
      <section className="py-20 bg-gradient-to-br from-brandNavy via-brandLightNavy to-brandNavy text-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="inline-block bg-brandRed px-6 py-3 rounded-full font-bold text-lg mb-6">
              🎁 LIMITED TIME OFFER
            </div>

            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              Experience Module 1 Completely FREE
            </h2>
            <p className="text-2xl md:text-3xl text-white/90 max-w-3xl mx-auto">
              Discover Your Million-Naira Sweet Spot
            </p>
          </div>

          <div className="bg-white text-brandNavy rounded-2xl p-8 md:p-12 mb-12">
            <h3 className="text-2xl md:text-3xl font-bold mb-8 text-center">
              What You'll Get in This FREE Session:
            </h3>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              {whatYouGet.map((item, i) => (
                <div
                  key={i}
                  className="bg-brandLightNavy/5 rounded-xl p-6 border-2 border-brandLightNavy/20"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start gap-3">
                      <span className="text-4xl">{item.icon}</span>
                      <div>
                        <h4 className="font-bold text-lg md:text-2xl">
                          {item.title}
                        </h4>
                        <div className="text-brandRed font-semibold text-base">
                          Value: {item.value}
                        </div>
                      </div>
                    </div>
                  </div>

                  <ul className="space-y-2">
                    {item.items.map((point, j) => (
                      <li
                        key={j}
                        className="flex items-start gap-2 text-mutedGray text-base"
                      >
                        <CheckIcon className="mt-1 flex-shrink-0" />
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <div className="border-t-2 border-dashed border-brandNavy/20 pt-8">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <div className="text-mutedGray mb-1 text-base">
                    Total Value:
                  </div>
                  <div className="text-3xl font-bold line-through text-mutedGray">
                    ₦90,000
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-mutedGray mb-1 text-base">
                    Your Investment Today:
                  </div>
                  <div className="text-5xl font-bold text-brandRed">₦0</div>
                  <div className="text-lg font-semibold text-green-600">
                    100% FREE
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 bg-blue-50 border-2 border-blue-300 rounded-xl p-6">
              <h4 className="font-bold text-lg md:text-2xl mb-3">Why Free?</h4>
              <p className="text-mutedGray text-base md:text-lg">
                Because I know once you see how simple this system is and
                experience the clarity it brings, you'll want to complete
                Modules 2 and 3 to build and convert your audience into income.
                But there's no obligation. Come to the FREE session. Get massive
                value. Decide later.
              </p>
            </div>
          </div>

          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8 text-center">
            <h3 className="text-2xl md:text-3xl font-bold mb-6">
              Next FREE Session Details
            </h3>

            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div>
                <div className="text-4xl mb-2">📅</div>
                <div className="font-semibold text-lg">15th December, 2025</div>
                <div className="text-white/70 text-base">7:00 PM WAT</div>
              </div>
              <div>
                <div className="text-4xl mb-2">📍</div>
                <div className="font-semibold text-lg">100% Online</div>
                <div className="text-white/70 text-base">via Zoom</div>
              </div>
              <div>
                <div className="text-4xl mb-2">⏱️</div>
                <div className="font-semibold text-lg">2.5 Hours</div>
                <div className="text-white/70 text-base">
                  Interactive Session
                </div>
              </div>
            </div>

            <div className="bg-red-500 text-white rounded-xl p-6 mb-6">
              <div className="text-sm md:text-base font-semibold mb-2">
                ⚠️ LIMITED AVAILABILITY
              </div>
              <div className="text-3xl font-bold mb-2">
                {spotsRemaining}/50 Spots Remaining
              </div>
              <div className="text-sm md:text-base">
                Maximum 50 participants for personalized attention
              </div>
            </div>

            <a
              href="#register"
              className="inline-block bg-brandRed text-white px-10 py-5 rounded-lg font-bold text-2xl hover:bg-red-700 transition-all transform hover:scale-105 shadow-xl"
            >
              YES! RESERVE MY FREE SPOT NOW →
            </a>

            <p className="text-white/70 text-base mt-4">
              No credit card required • Instant confirmation via email &
              WhatsApp
            </p>
          </div>
        </div>
      </section>

      {/* RESPONSE - Registration Form */}
      <section id="register" className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              Secure Your Spot in the Next FREE Session
            </h2>
            <p className="text-2xl md:text-2xl text-mutedGray">
              (Before They're Gone)
            </p>
          </div>

          <div className="bg-red-50 border-2 border-brandRed rounded-xl p-6 mb-8">
            <div className="flex flex-wrap justify-center gap-6 text-center">
              <div>
                <div className="font-bold text-brandRed text-base">
                  ⚠️ Only {spotsRemaining} spots remaining
                </div>
              </div>
              <div>
                <div className="font-bold text-brandRed text-base">
                  ⚠️ Last session sold out in 4 days
                </div>
              </div>
              <div>
                <div className="font-bold text-brandRed text-base">
                  ⚠️ Next session: [NEXT MONTH]
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12 border-2 border-brandNavy/10">
            <h3 className="text-2xl md:text-3xl font-bold mb-6 text-center">
              Here's What Happens Next:
            </h3>

            <div className="space-y-4 mb-8">
              {[
                {
                  step: 1,
                  text: "Fill in your details below (takes 30 seconds)",
                },
                {
                  step: 2,
                  text: "Receive immediate confirmation via email and WhatsApp",
                },
                {
                  step: 3,
                  text: "Show up LIVE ready to discover your million-naira sweet spot",
                },
                {
                  step: 4,
                  text: "Walk away with a validated business idea you can start testing THIS WEEK",
                },
              ].map((item) => (
                <div key={item.step} className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-brandRed text-white rounded-full flex items-center justify-center font-bold">
                    {item.step}
                  </div>
                  <div className="pt-2 text-mutedGray text-base md:text-lg">
                    {item.text}
                  </div>
                </div>
              ))}
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label
                  htmlFor="name"
                  className="block text-base font-semibold text-brandNavy mb-2"
                >
                  First Name *
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  required
                  value={formData.firstName}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-brandNavy/20 rounded-lg focus:border-brandRed focus:outline-none transition-colors text-base"
                  placeholder="Enter your first name"
                  disabled={!!successMessage || isLoading}
                />
              </div>

              <div>
                <label
                  htmlFor="email"
                  className="block text-base font-semibold text-brandNavy mb-2"
                >
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-brandNavy/20 rounded-lg focus:border-brandRed focus:outline-none transition-colors text-base"
                  placeholder="you@example.com"
                  disabled={!!successMessage || isLoading}
                />
              </div>

              <div>
                <label
                  htmlFor="phone"
                  className="block text-base font-semibold text-brandNavy mb-2"
                >
                  WhatsApp Number *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-brandNavy/20 rounded-lg focus:border-brandRed focus:outline-none transition-colors text-base"
                  placeholder="+234 xxx xxx xxxx"
                  disabled={!!successMessage || isLoading}
                />
              </div>

              {errorMessage && (
                <div className="text-center text-red-600 font-semibold">
                  {errorMessage}
                </div>
              )}

              {successMessage && (
                <div className="text-center text-green-700 font-semibold">
                  {successMessage}
                </div>
              )}

              <button
                type="submit"
                className="w-full bg-brandRed text-white px-8 py-5 rounded-lg font-bold text-2xl hover:bg-red-700 transition-all transform hover:scale-105 shadow-xl mb-4 disabled:opacity-60 disabled:cursor-not-allowed"
                disabled={!!successMessage || isLoading}
              >
                {isLoading
                  ? "Reserving your spot..."
                  : "SECURE MY FREE SPOT NOW →"}
              </button>

              <div className="mt-6 text-center text-base text-mutedGray">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <CheckIcon className="text-green-600" />
                  <span>Zero Risk • Zero Cost • Maximum Value</span>
                </div>
                <p>Your information is 100% secure and will never be shared.</p>
              </div>
            </form>
          </div>

          <div className="mt-12 bg-brandNavy text-white rounded-2xl p-8">
            <h3 className="text-2xl md:text-3xl font-bold mb-4 text-center">
              Zero Risk. Zero Cost. Maximum Value.
            </h3>
            <p className="text-white/90 text-center mb-6 text-base md:text-lg">
              <strong>This is not a sales pitch webinar.</strong> This is a real
              working session where you'll get hands-on help to:
            </p>

            <div className="grid md:grid-cols-2 gap-4">
              {[
                "Identify your most profitable skill",
                "Define your ideal customer",
                "Create an offer people will pay for",
                "Validate everything BEFORE you waste time building",
              ].map((benefit, i) => (
                <div key={i} className="flex items-start gap-3">
                  <CheckIcon className="text-brandRed mt-1 flex-shrink-0" />
                  <span className="text-base md:text-lg">{benefit}</span>
                </div>
              ))}
            </div>

            <p className="text-center mt-6 text-lg md:text-2xl font-semibold">
              Come get the clarity you've been missing. No strings attached.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-brandLightNavy/5">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            {faqs.map((faq, i) => (
              <div
                key={i}
                className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow"
              >
                <h3 className="font-bold text-lg md:text-2xl text-brandNavy mb-3">
                  {faq.q}
                </h3>
                <p className="text-mutedGray leading-relaxed text-base md:text-lg">
                  {faq.a}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-gradient-to-br from-brandNavy to-brandLightNavy text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
            Your Skills Are Worth More Than Your Salary. It's Time to Prove It.
          </h2>

          <p className="text-2xl md:text-2xl text-white/90 mb-12 max-w-3xl mx-auto">
            The only difference between you and the people making ₦500k+ monthly
            from online businesses is that they started.
          </p>

          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8 mb-12">
            <h3 className="text-2xl md:text-3xl font-bold mb-8">
              Your choice today:
            </h3>

            <div className="grid md:grid-cols-2 gap-8 text-left">
              <div className="bg-red-500/20 border-2 border-red-500 rounded-xl p-6">
                <div className="text-4xl mb-3">👎</div>
                <h4 className="font-bold text-lg md:text-2xl mb-3">
                  Option 1:
                </h4>
                <p className="text-white/90 text-base md:text-lg">
                  Close this page, go back to "researching," and be in the exact
                  same position 6 months from now (still trading time for money,
                  still worried about job security, still leaving millions on
                  the table).
                </p>
              </div>

              <div className="bg-green-500/20 border-2 border-green-500 rounded-xl p-6">
                <div className="text-4xl mb-3">👍</div>
                <h4 className="font-bold text-lg md:text-2xl mb-3">
                  Option 2:
                </h4>
                <p className="text-white/90 text-base md:text-lg">
                  Invest 30 seconds to reserve your free spot, show up for 2.5
                  hours, and walk away with a clear path to monetizing your
                  skills WHILE keeping your day job.
                </p>
              </div>
            </div>
          </div>

          <div className="mb-8">
            <p className="text-2xl md:text-3xl font-bold mb-4">
              Which option builds the future you actually want?
            </p>
          </div>

          <a
            href="#register"
            className="inline-block bg-brandRed text-white px-12 py-6 rounded-lg font-bold text-2xl hover:bg-red-700 transition-all transform hover:scale-105 shadow-2xl mb-12"
          >
            SECURE MY FREE SPOT NOW →
          </a>

          <div className="space-y-6 text-left max-w-3xl mx-auto">
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-6">
              <p className="font-semibold mb-2 text-lg md:text-2xl">
                P.S. — Still thinking about it?
              </p>
              <p className="text-white/90 text-base md:text-lg">
                Here's the reality: Last month's FREE session had a waitlist.
                The month before, people were begging for spots after it filled
                up. Spots are first-come, first-served. If you're reading this
                and there are still spots available, you're lucky. Don't gamble
                on spots being available tomorrow.
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-6">
              <p className="font-semibold mb-2 text-lg md:text-2xl">
                P.P.S. — Remember, this is FREE.
              </p>
              <p className="text-white/90 text-base md:text-lg">
                Zero financial risk. The only risk is staying exactly where you
                are—talented, skilled, and underearning. Click the button. See
                you at the session.
              </p>
            </div>
          </div>

          <div className="mt-12">
            <a
              href="#register"
              className="inline-block bg-white text-brandNavy px-10 py-5 rounded-lg font-bold text-2xl hover:bg-gray-100 transition-all transform hover:scale-105 shadow-xl"
            >
              I'M READY — RESERVE MY SPOT →
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-brandNavy text-white py-8">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <p className="text-white/70 text-sm">
            © {new Date().getFullYear()} D.B.C. Formula. All rights reserved.
          </p>
          <div className="mt-4 flex justify-center gap-6 text-sm">
            <Link
              to="/privacy"
              target="_blank"
              className="text-white/70 hover:text-white"
            >
              Privacy Policy
            </Link>
            <Link
              to="/terms"
              target="_blank"
              className="text-white/70 hover:text-white"
            >
              Terms of Service
            </Link>
            <Link
              to="/contact"
              target="_blank"
              className="text-white/70 hover:text-white"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DBCModule1;
